import { useState, useEffect } from 'react';
import { Menu, X, Phone, Instagram } from 'lucide-react';
import { WHATSAPP_URL, LOGO_ONLY, INSTAGRAM_URL } from '../constants';

const navLinks = [
  { label: 'Sobre', href: '#sobre' },
  { label: 'Hiperidrose', href: '#hiperidrose' },
  { label: 'Procedimento', href: '#procedimento' },
  { label: 'Depoimentos', href: '#depoimentos' },
  { label: 'Convênios', href: '#convenios' },
  { label: 'FAQ', href: '#faq' },
  { label: 'Contato', href: '#contato' },
];

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  const handleNavClick = () => setIsOpen(false);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-xl shadow-sm border-b border-gray-100'
          : 'bg-white/80 backdrop-blur-xl border-b border-gray-100'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 lg:px-8 flex items-center justify-between h-16">
        {/* Logo */}
        <a href="#home" className="flex items-center gap-2 flex-shrink-0" data-testid="logo-link">
          <div className="h-10 w-10 rounded-xl overflow-hidden bg-black flex-shrink-0">
            <img src={LOGO_ONLY} alt="Logo Dr. Mateus Marques" className="w-full h-full object-cover" />
          </div>
          <div className="hidden sm:block">
            <div className="font-poppins font-bold text-[#0D47A1] text-sm leading-tight">Dr. Mateus Marques</div>
            <div className="text-xs text-[#00897B] font-semibold">Cirurgião Torácico</div>
          </div>
        </a>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="text-sm text-[#212121]/70 hover:text-[#0D47A1] font-medium px-3 py-2 rounded-lg hover:bg-[#0D47A1]/5 transition-all duration-200"
              data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {link.label}
            </a>
          ))}
        </nav>

        {/* CTA + Instagram + Mobile Menu */}
        <div className="flex items-center gap-2">
          <a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-instagram-link"
            aria-label="Instagram"
            className="hidden sm:flex items-center justify-center w-9 h-9 rounded-full text-[#212121]/60 hover:text-[#E1306C] hover:bg-[#E1306C]/10 transition-all duration-200"
          >
            <Instagram size={18} />
          </a>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-cta-button"
            className="hidden sm:flex items-center gap-2 bg-[#0D47A1] text-white font-poppins font-semibold text-sm px-5 py-2.5 rounded-full hover:bg-[#00897B] transition-colors duration-200 whitespace-nowrap"
          >
            <Phone size={14} />
            AGENDAR CONSULTA
          </a>
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 rounded-lg text-[#212121] hover:bg-gray-100 transition-colors"
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {isOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div
          className="lg:hidden bg-white border-t border-gray-100 shadow-lg"
          data-testid="mobile-nav"
        >
          <nav className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-1">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={handleNavClick}
                className="text-[#212121]/80 hover:text-[#0D47A1] font-medium py-3 px-3 rounded-lg hover:bg-[#0D47A1]/5 transition-all duration-200"
                data-testid={`mobile-nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </a>
            ))}
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleNavClick}
              data-testid="mobile-cta-button"
              className="mt-2 flex items-center justify-center gap-2 bg-[#0D47A1] text-white font-poppins font-semibold py-3 px-6 rounded-full hover:bg-[#00897B] transition-colors"
            >
              <Phone size={16} />
              AGENDAR CONSULTA
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
